import { globalStores } from '@/editor/stores';
import BaseModel2d from './BaseModel2d';
import { ENTITY_PRIORITY, ENTITY_RENDER_ORDER, hoverOutlineColor } from '@/editor/constant/entity';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { EEntityType } from '@/editor/types';
import { EContainerType } from '@/editor/types/entity';
import { disposeAllChildren, disposeAllChildrenThenRemoveFromParent } from '../utils';
import { BufferGeometry, Float32BufferAttribute, LineBasicMaterial, LineSegments } from 'three';
import { pixelRatio2d, thresholdValue } from '@/editor/math/const';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { formatLinearDimensionPoint } from '@/editor/data/dimension/utils';
import { ILinearDimension } from '@/editor/data/dimension/dimension';
import { LinearDimensionTextInfo } from './textInfo';
import {
  LINEAR_DIMENSION_ASSIST_LINE_OFFSET,
  LINEAR_DIMENSION_ASSIST_SLASH_LINE_OFFSET,
  LINEAR_DIMENSION_TEXT_OFFSET
} from '@/editor/constant/dimension';

const MODEL_COLOR = {
  selected: 0x1a7af8,
  standard: 0xdf4545,
  hover: hoverOutlineColor
} as const;

const VIRTUAL_ID = 'virtual_linear_dimension';

export class LinearDimensionModel extends BaseModel2d {
  private _modelColor: number = MODEL_COLOR.standard;
  private _id: string;
  private _textModel: LinearDimensionTextInfo;
  private _scale: number = 1;
  private _hover: boolean = false;
  private _selected: boolean = false;

  constructor(data: Partial<ILinearDimension>) {
    super();
    this._id = data.id ?? VIRTUAL_ID;

    this._initEntityProperty(this._id, !!data.id);
    globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._modelGroup);
    this._textModel = new LinearDimensionTextInfo({
      onDelete: () => globalStores.dimensionOperation.deleteLinearDimension(this._id)
    });

    this.updateModel(data);
  }

  static convertPointsToLinearDimension(points: IPoint2d[]) {
    const { offset } = formatLinearDimensionPoint(points);

    return { start: points[0], end: points[1], offset };
  }

  get hover() {
    return this._hover;
  }

  set hover(hover: boolean) {
    this._hover = hover;
  }

  setSelected(selected: boolean) {
    this._selected = selected;
  }

  private _refreshModelColor() {
    if (this._hover) {
      this._modelColor = MODEL_COLOR.hover;
      return;
    }
    if (this._selected) {
      this._modelColor = MODEL_COLOR.selected;
      return;
    }
    this._modelColor = this._id === VIRTUAL_ID ? MODEL_COLOR.selected : MODEL_COLOR.standard;
  }

  private _initEntityProperty(id: string, intersected?: boolean) {
    this._modelGroup.userData.containerType = intersected ? EContainerType.entity : EContainerType.noIntersectable;
    this._modelGroup.userData.entityId = id;
    this._modelGroup.userData.type = EEntityType.LinearDimension;
    this._modelGroup.userData.priority = ENTITY_PRIORITY.linearDimension;
    this.setDisplayOrder(ENTITY_RENDER_ORDER.ruler);
  }

  updateModel(data: Partial<ILinearDimension>) {
    this.clear();
    this._refreshModelColor();

    const { start, end, offset = 0 } = data;
    if (start && end) {
      this._createLineModel(start, end, offset);
      this._textModel.setSelected(this._selected);
    }
  }

  updateScale(data: Partial<ILinearDimension>, scale: number) {
    this._scale = scale;
    this.updateModel(data);
  }

  private _createLineModel(startPoint: IPoint2d, endPoint: IPoint2d, offset: number) {
    // 起点与终点重合时方向向量退化为 0/0=NaN，会污染整个 position 数组并导致
    // computeBoundingSphere() 计算出 NaN 半径，此时无需构建标注几何体，直接跳过。
    if (Point2dUtils.distanceTo(startPoint, endPoint) < thresholdValue) {
      return;
    }

    const verticalDirection = new LineSeg2d(startPoint, endPoint).getPerpendicularDirectionVector();
    const rulerStart = Point2dUtils.addDistance(startPoint, offset, verticalDirection);
    const rulerEnd = Point2dUtils.addDistance(endPoint, offset, verticalDirection);
    const rulerLineSeg = new LineSeg2d(rulerStart, rulerEnd);

    // 源线指向尺寸线的朝外法向（等价于 sign(offset)·verticalDirection）。辅助线延长与文字偏移共用此方向，
    // 保证两者无论 offset 正负都落在标注最外侧；offset≈0 退化时回退到 -verticalDirection。
    const outwardDir = getOutwardDirection(
      Point2dUtils.getCenter(startPoint, endPoint),
      rulerLineSeg.getSegmentCenter(),
      verticalDirection
    );

    const rulerPoints = getRulerSegPoints(rulerLineSeg, {
      sourceStart: startPoint,
      sourceEnd: endPoint,
      outwardDir,
      assistLineOffsetValue: LINEAR_DIMENSION_ASSIST_LINE_OFFSET,
      assistSlashLineOffsetValue: LINEAR_DIMENSION_ASSIST_SLASH_LINE_OFFSET
    });

    // 使用三维坐标(z=0)，否则 Three.js 的射线检测会因 itemSize=2 读取到错误的 z 分量而无法命中
    const positions = new Float32Array(rulerPoints.length * 3);
    for (let i = 0; i < rulerPoints.length; i++) {
      positions[i * 3] = rulerPoints[i].x;
      positions[i * 3 + 1] = rulerPoints[i].y;
      positions[i * 3 + 2] = 0;
    }

    this._updateTextModel(rulerLineSeg, outwardDir);

    const lineGmt = new BufferGeometry();
    lineGmt.setAttribute('position', new Float32BufferAttribute(positions, 3));
    // 计算真实包围球，供射线检测的粗筛阶段使用，否则标注会被错误剔除
    lineGmt.computeBoundingSphere();

    const line = new LineSegments(lineGmt, new LineBasicMaterial({ color: this._modelColor }));
    line.frustumCulled = false;
    this._modelGroup.add(line);
  }

  /**
   * 更新标注文字：文字沿朝外法向偏移，恒落在标注最外侧（越过尺寸线），不进入源线与尺寸线之间的区域。
   * @param rulerLineSeg 尺寸线线段
   * @param outwardDir 源线指向尺寸线的朝外法向单位向量
   */
  private _updateTextModel(rulerLineSeg: LineSeg2d, outwardDir: IPoint2d) {
    const rulerMid = rulerLineSeg.getSegmentCenter();

    this._textModel.createModel({
      position: Point2dUtils.addDistance(rulerMid, LINEAR_DIMENSION_TEXT_OFFSET * Math.min(1, this._scale), outwardDir),
      id: this._id,
      value: Point2dUtils.distanceTo(rulerLineSeg.start, rulerLineSeg.end).toFixed(0),
      rotation: getReadableTextRotation(rulerLineSeg)
    });
  }

  public updateTextView() {
    this._textModel.viewChange();
  }

  public updateTextVisible(visible: boolean) {
    this._textModel.setVisible(visible);
    if (visible) this._textModel.updatePosition();
  }

  override clear() {
    this._textModel.clear();
    disposeAllChildren(this._modelGroup);
  }

  override dispose() {
    this._textModel.dispose();
    disposeAllChildrenThenRemoveFromParent(this._modelGroup);
  }
}

/**
 * 计算标注文字的旋转角度（弧度，供 CSS rotate 使用），保证文字在任意标注方向下都保持正向可读。
 *
 * 取线段两端中 x 最小（x 相等即竖直线时取 y 最小，均为 world 坐标）的一端作为文字起始端，
 * 文字沿"起始端 -> 另一端"排布。该方向 x 分量恒 ≥ 0（读向朝右），竖直标注则自下而上。
 * world 角经屏幕 y 翻转后即为 CSS 旋转角，故取其相反数。
 */
function getReadableTextRotation(seg: LineSeg2d): number {
  const { start, end } = seg;
  const startIsMinCorner = Math.abs(start.x - end.x) > thresholdValue ? start.x < end.x : start.y <= end.y;
  const [from, to] = startIsMinCorner ? [start, end] : [end, start];

  return -Math.atan2(to.y - from.y, to.x - from.x);
}

/**
 * 计算源点指向偏移点的朝外法向（单位向量），用于辅助线的延长方向与文字的偏移方向。
 * 取源点 -> 偏移点的实际方向（避免固定用 verticalDirection 时因 offset 为负导致方向反向）；
 * 当两点重合（偏移量为 0）无法确定方向时，回退到 -verticalDirection。
 */
function getOutwardDirection(source: IPoint2d, offsetPoint: IPoint2d, verticalDirection: IPoint2d): IPoint2d {
  const dir = Point2dUtils.subtracted(offsetPoint, source);
  const length = Math.hypot(dir.x, dir.y);
  if (length < thresholdValue) {
    return { x: -verticalDirection.x, y: -verticalDirection.y };
  }

  return { x: dir.x / length, y: dir.y / length };
}

/**
 * 构建标注几何体的顶点，按线段成对返回（供 LineSegments 使用），坐标已转换到渲染空间。
 * 依次为：尺寸线主线、起点辅助线、起点 45° 斜线、终点辅助线、终点 45° 斜线，共 5 段 10 个点。
 */
function getRulerSegPoints(
  offsetLine: LineSeg2d,
  options: {
    sourceStart: IPoint2d;
    sourceEnd: IPoint2d;
    outwardDir: IPoint2d;
    assistLineOffsetValue: number;
    assistSlashLineOffsetValue: number;
  }
): IPoint2d[] {
  const direction = offsetLine.getDirectionVector();
  const verticalDirection = offsetLine.getPerpendicularDirectionVector();
  const { sourceStart, sourceEnd, outwardDir, assistLineOffsetValue, assistSlashLineOffsetValue } = options;

  // 以偏移端点为中心的 45° 斜线：沿 ±direction、±verticalDirection 各偏移半长得到两个端点
  const slashSeg = (center: IPoint2d): [IPoint2d, IPoint2d] => [
    Point2dUtils.addDistance(
      Point2dUtils.addDistance(center, assistSlashLineOffsetValue, direction),
      assistSlashLineOffsetValue,
      verticalDirection
    ),
    Point2dUtils.addDistance(
      Point2dUtils.addDistance(center, -assistSlashLineOffsetValue, direction),
      -assistSlashLineOffsetValue,
      verticalDirection
    )
  ];

  const segments: [IPoint2d, IPoint2d][] = [
    // 尺寸线主线
    [offsetLine.start, offsetLine.end],
    // 起点辅助线：源点 -> 越过偏移起点延长
    [sourceStart, Point2dUtils.addDistance(offsetLine.start, assistLineOffsetValue, outwardDir)],
    // 起点辅助斜线
    slashSeg(offsetLine.start),
    // 终点辅助线：源点 -> 越过偏移终点延长
    [sourceEnd, Point2dUtils.addDistance(offsetLine.end, assistLineOffsetValue, outwardDir)],
    // 终点辅助斜线
    slashSeg(offsetLine.end)
  ];

  return segments.flat().map((point) => ({ x: point.x / pixelRatio2d, y: point.y / pixelRatio2d }));
}
